<template>
  <div class="gmf-my-content gmf-context">
    <template v-if="! hideDescription">
      <h2 class="gmf-header" v-if="t('my_content')">
        {{ t('my_content') }}
      </h2>
      <p v-if="t('my_content_description')">{{ t('my_content_description') }}</p>
    </template>
    <DropdownSelect
      v-model="filterSelected"
      :options="commentTypes"
    />
    <div v-for="comment in myComments" :key="comment.id" class="gmf-clickable" @click="ev => onClick(ev, comment)">
      <Comment
        :disableProfilePopup="disableProfilePopup"
        :comment="comment"
        showDelete="true"
        :deleteMethod="deleteComment"
        :voteMode="voteMode"
        :depth="0"
        :elements="{
          avatar: false,
          footer: true,
          customerLevel: false,
          customerAttributes: false,
          uploads: true,
          prosAndCons: true,
          action: true,
          productImage: true,
          targetImage: showTargetImages,
          target: false,
          reply: false,
        }"
      />
    </div>
    <template v-if="myComments.length == 0">
      <p>{{ noCommentsPhrase }}</p>
    </template>

    <InViewport @enter="autoload" rootMargin="250px">
      <button
        class="gmf-btn gmf-btn-primary gmf-btn-sm gmf-show-more-comments"
        v-show="hasMoreContent && ! isLoading"
        @click="loadNextPage"
      >
        {{ t('show_more_comments') }} ({{ remainingComments }})
      </button>
    </InViewport>
  </div>
</template>

<style>
.gmf-my-content {
  min-height: 360px;
}
.gmf-my-content .gmf-dropdown {
  margin-bottom: var(--gmf-object-spacing);
}
#gmf-my-content .gmf-dropdown {
  max-width: 300px;
}

.gmf-my-content .gmf-comment-customer-col {
  width: 50px;
}
</style>

<script>
import Comment from '@/components/Comment';
import callApi from '@/utils/callApi';

//import batchFunction from '../utils/batchFunction';

import state from '@/state';
import DropdownSelect from '@/components/DropdownSelect';
import InViewport from '@/components/InViewport';
import openPostModal from '@/widgets/PostModal/openPostModal'

const t = state.t.prefix('comments');

export default {
  props: {
    hideDescription: {
      Type: Boolean,
      default: false,
    },
    showTargetImages: {
      Type: Boolean,
      default: true,
    },
    /**
     * Provide external customerId here to load that customer's comments.
     */
    customerId: {
      Type: Number,
      default: null,
    },
    disableProfilePopup: {
      Type: Boolean,
      default: false,
    },
  },
  components: {
    Comment,
    DropdownSelect,
    InViewport
},
  data() {
    return {
      state,
      targetCustomerId: null, // Comments will be loaded from this customer id.
      myComments: [],
      voteMode: "",
      filterSelected: '',
      isLoading: false,
      totalCount: 0,
      remainingComments: 0,
      itemsPerPage: 8,
      deleteOffset: 0,
      page: 0,
      commentTypes: [ 'product_review', 'product_question', 'product_hype', 'product_discussion', 'social_post', 'site_review', 'marketplace' ],
    };
  },
  computed: {
    autoloadEnabled() {
      return this.state.merchantConfig?.widgets?.myComments?.autoload;
    },
    merchantOptions() {
      const mc = this.state.merchantConfig.comments;
      const res = [];
      for (const key in mc) {
        if (mc[key].enabled) {
          res.push(key);
        }
      }
      return res;
    },
    hasMoreContent () {
      return this.remainingComments > 0;
    },
    // True when you are viewing another person's comment list.
    isOtherCustomer () {
      return this.customerId !== null;
    },
    noCommentsPhrase () {
      return this.isOtherCustomer ? t('customer_comments_empty') : t('my_comments_empty');
    },
    profile() {
      return this.state.profile;
    },
    translationsEnabled() {
      return this.state.merchantConfig?.widgets?.translations.mode !== 'translateNothing';
    },
  },
  async mounted() {
    this.voteMode = this.state.merchantConfig.voteMode;

    if (this.isOtherCustomer) {
      // Customer id provided as a prop
      this.targetCustomerId = this.customerId;
    } else {
      // Component used as widget.
      const profile = this.profile;
      if (profile) {
        this.targetCustomerId = profile.id;
      }
    }

    await this.loadNextPage();

    // Add validated comment types to DropdownSelect
    const formattedCommentTypes = [];
    for (const type of this.commentTypes) {
      if (this.validMerchantOptions(type)) {
        formattedCommentTypes.push({
          label: this.capitalize(t('comment_type_name.'+type)),
          value: type
        });
      }
    }
    this.commentTypes = formattedCommentTypes;
    // Add default value
    this.commentTypes.unshift({ label: this.capitalize(t('comment_type_name.all_content')), value: '' });
  },
  methods: {
    t,
    onClick (ev, comment) {
      ev.stopPropagation();
      ev.preventDefault();
      openPostModal(comment.id, 0, this.myComments.map(c => c.id), this.loadNextPage);
      return false;
    },
    validMerchantOptions(opt) {
      return this.merchantOptions.includes(opt);
    },
    capitalize(s) {
      if (! s) {
        return '';
      }
      return s[0].toUpperCase() + s.slice(1);
    },
    async deleteComment(comment) {
      callApi('DELETE', '/v1/client/comments/' + comment.id)
        .then(res => {
          if (res.status == 200) {
            // to remove it as an reply
            this.myComments.forEach(e => {
              e.replies = e.replies.filter(o => o.id != comment.id);
            });

            let l0 = this.myComments.length;

            this.myComments = this.myComments.filter(o => {
              // to remove it as an comment
              if (o.id == comment.id) {
                return false;
              }
              // to remove parent comments that lost its last reply
              if (o.author.id !== this.targetCustomerId && o.replies.length == 0) {
                return false;
              }
              return true;
            });

            let l1 = this.myComments.length;

            if (comment.replies.length == 0) {
              this.deleteOffset -= l0 - l1;
            }
          }
        })
        .catch(error => {
          console.log(error);
        });
    },
    autoload () {
      if (! this.autoloadEnabled) {
        return;
      }
      if (! this.hasMoreContent) {
        return;
      }
      this.loadNextPage();
    },
    async loadNextPage() {
      this.isLoading = true;
      let skip = this.itemsPerPage * this.page + this.deleteOffset;
      let type = this.filterSelected ? '&type='+this.filterSelected : '';
      
      const query = Object.assign({}, this.query, {
        customer_id: this.targetCustomerId,
        include: 'author',
        order: 'created.desc',
        include_orphans: 1,
        take: this.itemsPerPage,
        skip: skip,
        translate: this.translationsEnabled,
      });

      const res = await callApi(
        'GET',
        this.state.commentSearchEndpoint + `?${new URLSearchParams(query).toString()}${type}`
      );

      if (res.status == 200) {
        const data = await res.json();
        if (data.comments) {
          this.myComments.push(...data.comments);
          this.totalCount = data.totalCount;
          this.remainingComments = this.totalCount - this.myComments.length || 0;
          this.deleteOffset = 0;
        }
      } else if (res.status == 401) {
        console.error('Gamifiera client not authorized');
        return;
      }
      this.page++;
      this.isLoading = false;
    }
  },
  watch: {
    filterSelected() {
      this.myComments = [];
      this.page = 0;

      this.loadNextPage();
    }
  }
};
</script>